/**
 * Os primitivos do painel — o design system inteiro, num arquivo.
 *
 * Poucos componentes, sem biblioteca: foi a decisão de stack do plano (Tailwind + componentes
 * próprios em vez do CLI do shadcn). O preço dessa escolha é que consistência vira disciplina, e
 * disciplina se perde. Por isso as decisões estão aqui como **constantes**, não como classes
 * repetidas em vinte arquivos: mudar o raio de um card é mudar `RAIO.card`, não caçar `rounded-xl`.
 *
 * As quatro escalas que este arquivo define, e que os testes `tipografia`, `espacamento` e `cores`
 * protegem:
 *
 * - **Tipografia** — dois degraus de leitura (12/14) e três de título (16/18/24). Ver `PROSA`.
 * - **Espaçamento** — múltiplos de 4px, só os degraus inteiros do Tailwind. Sem `py-2.5`.
 * - **Raio** — três: 6px em selo, 8px em controle, 12px em card. Ver `RAIO`.
 * - **Cor** — estado e categoria vivem em famílias separadas. Ver `Tone`.
 */

import {
  useRef,
  type ChangeEvent,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { Link } from 'react-router-dom';
import {
  IconeAlerta,
  IconeCheck,
  IconeErro,
  IconeInfo,
  IconeLixeira,
  IconeMais,
  type IconProps,
} from './icons.js';

/**
 * Anel de foco visível, em `outline` e não em `ring`.
 *
 * O que havia antes era `outline-none` com `ring-slate-200`: cinza clarinho sobre branco, uns 1,2:1
 * de contraste, quando a WCAG 2.2 pede 3:1 para indicador de foco. Ou seja, tirava o anel do
 * navegador e punha no lugar algo que não dá para enxergar — navegar por Tab virava adivinhação.
 *
 * `outline` em vez de `ring` porque outline não entra no fluxo de layout nem é recortado por
 * `overflow: hidden` do pai.
 */
const FOCO = 'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900';

/**
 * O estado desabilitado é **um só** para todas as variantes.
 *
 * Antes cada uma cuidava do seu, e duas não cuidavam: `ghost` e `danger` não tinham nenhuma classe
 * `disabled:`. Um "Remover" desabilitado — o caso real, quando resta uma única opção — ficava
 * idêntico a um habilitado, mudando só o cursor.
 */
const DESABILITADO = 'disabled:cursor-not-allowed disabled:opacity-45';

/**
 * Escala tipográfica do painel.
 *
 * O painel comunicava quase tudo em 12px, com casos de 10 e 11. Sem diferença de tamanho não há
 * hierarquia: rótulo, aviso de segurança e metadado competiam igual, e a tela virava um bloco
 * cinza uniforme. Era a medida objetiva do "está poluído".
 *
 * - `text-xs` (12px) — rótulo de campo, selo, metadado. Palavra solta, não frase.
 * - `text-sm` (14px) — **tudo que é frase para ler**: hint, aviso, descrição, corpo de tabela.
 * - `text-base` (16px) — título de seção e de card.
 * - `text-lg` (18px) — título de área dentro do editor.
 * - `text-2xl` (24px) — título de página e número de indicador.
 *
 * Nada abaixo de 12px. A única exceção documentada são os selos do nó no canvas (`BlockNode`), onde
 * são palavras de cinco letras em caixa alta dentro de 256px — e existe teste fixando que a exceção
 * não se espalhe.
 */
const PROSA = 'text-sm leading-snug';

/**
 * Três raios, e o motivo de cada um: o raio comunica **tamanho do objeto**, então precisa crescer
 * junto com ele. Selo pequeno com raio de card parece uma bolha; card grande com raio de selo parece
 * um retângulo. Misturar os dois foi o que deixou a tela sem sotaque próprio.
 */
export const RAIO = {
  /** Selo, chip, ícone pequeno. */
  selo: 'rounded-md',
  /** Botão, campo, item de lista. */
  controle: 'rounded-lg',
  /** Card, painel, menu suspenso. */
  card: 'rounded-xl',
  /** Pílula: badge e barra de progresso. */
  pilula: 'rounded-full',
} as const;

/**
 * Elevação em dois degraus, nada além.
 *
 * `SOMBRA.card` é deliberadamente dupla: uma camada curta de contato e uma longa de ambiente. Uma
 * sombra só, difusa, deixa o card boiando; duas fazem ele pousar. É a mesma receita do nó no canvas,
 * onde o problema apareceu primeiro — borda quase invisível sobre fundo quase branco.
 */
export const SOMBRA = {
  card: 'shadow-[0_1px_2px_rgba(15,23,42,0.06),0_4px_12px_-2px_rgba(15,23,42,0.10)]',
  menu: 'shadow-[0_4px_6px_-2px_rgba(15,23,42,0.08),0_16px_32px_-8px_rgba(15,23,42,0.22)]',
} as const;

/**
 * Tom de estado — e **só** de estado.
 *
 * O painel usava `sky`, `emerald`, `amber` e `rose` para duas coisas ao mesmo tempo: o tom de um
 * aviso e a categoria de um tipo de bloco. Âmbar era "atenção" no `Notice` e "Condição" no canvas;
 * esmeralda era "deu certo" no selo e "webhook de backend" no nó. Cor que significa duas coisas não
 * significa nenhuma — a pessoa para de ler cor e passa a ler só texto, que é o desperdício exato que
 * uma paleta existe para evitar.
 *
 * A divisão agora:
 *
 * - **Estado** (aqui) — `sky` informa, `emerald` confirma, `amber` alerta, `red` recusa, `slate` é
 *   o neutro.
 * - **Categoria** (`BLOCK_FAMILIES`, em `flow/mapping.ts`) — `violet` para lógica, `indigo` para
 *   integração, cinza para conversa. Três famílias, não sete: o **ícone** distingue os sete tipos,
 *   e a cor responde à pergunta que se faz varrendo o canvas de longe, que é "onde estão minhas
 *   integrações".
 *
 * `cores.test.ts` recusa uma categoria que invada família de estado.
 *
 * Os nomes ficam em inglês porque `tone`, `variant` e `checked` também estão — o comentário é que é
 * em português, seguindo o resto do projeto.
 */
export type Tone = 'neutral' | 'info' | 'ok' | 'warn' | 'danger';

/** Ícone de cada tom. Existe para o tom não ser comunicado **só** por cor (WCAG `color-not-only`). */
const ICONE_DO_TOM: Record<Tone, (props: IconProps) => ReactNode> = {
  neutral: IconeInfo,
  info: IconeInfo,
  ok: IconeCheck,
  warn: IconeAlerta,
  danger: IconeErro,
};

/** Cor sólida do tom, para ponto e barra. */
const SOLIDO_DO_TOM: Record<Tone, string> = {
  neutral: 'bg-slate-400',
  info: 'bg-sky-500',
  ok: 'bg-emerald-500',
  warn: 'bg-amber-500',
  danger: 'bg-red-500',
};

// --- botões ----------------------------------------------------------------

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';

const BUTTON_STYLES: Record<ButtonVariant, string> = {
  primary: 'bg-slate-900 text-white hover:bg-slate-800',
  secondary: 'bg-white text-slate-800 border border-slate-300 hover:border-slate-400 hover:bg-slate-50',
  ghost: 'bg-transparent text-slate-600 hover:bg-slate-100 hover:text-slate-900',
  /*
   * `red-700` e não `red-600`: sobre branco, o 600 dá 4,0:1 e o 700 dá 6,2:1. A diferença mal se vê
   * e é a que separa reprovado de aprovado na WCAG AA.
   */
  danger: 'bg-white text-red-700 border border-red-300 hover:border-red-400 hover:bg-red-50',
};

/**
 * Altura fixa, e não padding vertical.
 *
 * Com `py-*`, um botão de texto e um botão com ícone terminavam com alturas diferentes por causa da
 * altura de linha — e lado a lado numa barra isso lê como desalinhamento. Altura fixa resolve na
 * origem e de quebra garante um alvo de clique previsível.
 */
const BUTTON_SIZES = {
  md: 'h-9 px-3 text-sm',
  sm: 'h-8 px-3 text-xs',
} as const;

type ButtonSize = keyof typeof BUTTON_SIZES;

const BUTTON_BASE = `inline-flex shrink-0 items-center justify-center gap-2 font-medium transition ${RAIO.controle} ${FOCO} ${DESABILITADO}`;

export function Button({
  children,
  variant = 'secondary',
  size = 'md',
  className = '',
  ...rest
}: {
  children: ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  className?: string;
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      {...rest}
      className={`${BUTTON_BASE} ${BUTTON_SIZES[size]} ${BUTTON_STYLES[variant]} ${className}`}
    >
      {children}
    </button>
  );
}

/**
 * Botão só de ícone. O `label` é **obrigatório** e vira `aria-label` e `title`.
 *
 * Sem essa obrigação, ícone sozinho vira botão anônimo: o leitor de tela anuncia "botão" e o mouse
 * não recebe dica nenhuma. Exigir no tipo é mais barato que lembrar.
 */
export function IconButton({
  icon: Icone,
  label,
  variant = 'ghost',
  size = 'md',
  className = '',
  ...rest
}: {
  icon: (props: IconProps) => ReactNode;
  label: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
  className?: string;
} & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'>) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      {...rest}
      className={`${BUTTON_BASE} ${size === 'md' ? 'h-9 w-9' : 'h-8 w-8'} ${BUTTON_STYLES[variant]} ${className}`}
    >
      <Icone size={size === 'md' ? 17 : 15} />
    </button>
  );
}

/**
 * Link com cara de botão.
 *
 * O painel tinha `<Link><Button/></Link>` em três lugares: uma âncora envolvendo um `<button>`. É
 * HTML inválido (conteúdo interativo dentro de conteúdo interativo), o Tab para nos dois, e o leitor
 * de tela anuncia link e botão para a mesma coisa. Um elemento só — o `<a>` — resolve, e preserva o
 * Ctrl+clique, que era a razão de ser um link.
 */
export function LinkButton({
  to,
  children,
  variant = 'secondary',
  size = 'md',
  className = '',
  ...rest
}: {
  to: string;
  children: ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  className?: string;
} & Omit<React.ComponentProps<typeof Link>, 'to' | 'className' | 'children'>) {
  return (
    <Link
      to={to}
      {...rest}
      className={`${BUTTON_BASE} ${BUTTON_SIZES[size]} ${BUTTON_STYLES[variant]} ${className}`}
    >
      {children}
    </Link>
  );
}

// --- campos ----------------------------------------------------------------

export function Field({
  label,
  hint,
  children,
  error,
}: {
  label: string;
  hint?: ReactNode;
  children: ReactNode;
  error?: string | null;
}) {
  return (
    <label className="block space-y-2">
      <span className="block text-xs font-semibold tracking-wide text-slate-600 uppercase">{label}</span>
      {children}
      {hint && !error ? <span className={`block ${PROSA} text-slate-500`}>{hint}</span> : null}
      {/*
        `role="alert"` para o leitor de tela anunciar o erro no momento em que ele aparece. Sem isso,
        quem não enxerga a tela submete o formulário e não recebe retorno nenhum.
      */}
      {error ? (
        <span role="alert" className={`block ${PROSA} text-red-700`}>
          {error}
        </span>
      ) : null}
    </label>
  );
}

const CONTROL = `w-full border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 transition placeholder:text-slate-400 focus:border-slate-500 ${RAIO.controle} ${FOCO}`;

export function Input(props: InputHTMLAttributes<HTMLInputElement>) {
  const { className = '', ...rest } = props;
  return <input {...rest} className={`${CONTROL} ${className}`} />;
}

export function Textarea(props: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const { className = '', ...rest } = props;
  return <textarea {...rest} className={`${CONTROL} font-mono text-sm leading-relaxed ${className}`} />;
}

export function Select(props: SelectHTMLAttributes<HTMLSelectElement>) {
  const { className = '', children, ...rest } = props;
  return (
    <select {...rest} className={`${CONTROL} ${className}`}>
      {children}
    </select>
  );
}

export function Switch({
  checked,
  onChange,
  label,
  description,
  tone = 'neutral',
}: {
  checked: boolean;
  onChange(next: boolean): void;
  label: string;
  description?: ReactNode;
  tone?: 'neutral' | 'warning';
}) {
  return (
    <div
      className={`border p-3 ${RAIO.controle} ${
        checked && tone === 'warning' ? 'border-amber-300 bg-amber-50' : 'border-slate-200 bg-white'
      }`}
    >
      <label className="flex cursor-pointer items-start gap-3">
        {/*
          A caixa vai dentro de um container da altura da primeira linha de texto (20px), centrada
          nele. Antes era um `mt-0.5` medido a olho — que é como espaçamento fora de escala entra.
        */}
        <span className="flex h-5 shrink-0 items-center">
          <input
            type="checkbox"
            checked={checked}
            onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event.target.checked)}
            className={`h-4 w-4 accent-slate-900 ${FOCO}`}
          />
        </span>
        <span className="space-y-1">
          <span className="block text-sm font-medium text-slate-800">{label}</span>
          {description ? <span className={`block ${PROSA} text-slate-600`}>{description}</span> : null}
        </span>
      </label>
    </div>
  );
}

// --- sinalização -----------------------------------------------------------

const BADGE_TONES: Record<Tone, string> = {
  neutral: 'bg-slate-100 text-slate-700',
  info: 'bg-sky-100 text-sky-800',
  ok: 'bg-emerald-100 text-emerald-800',
  warn: 'bg-amber-100 text-amber-900',
  danger: 'bg-red-100 text-red-800',
};

/**
 * Selo curto. `dot` acende um ponto na cor do tom — usado no status de salvamento, onde o que
 * importa é ser percebido pelo canto do olho sem roubar atenção.
 */
export function Badge({
  children,
  tone = 'neutral',
  dot = false,
}: {
  children: ReactNode;
  tone?: Tone;
  dot?: boolean;
}) {
  return (
    <span
      className={`inline-flex items-center gap-2 px-2 py-1 text-xs leading-none font-semibold ${RAIO.pilula} ${BADGE_TONES[tone]}`}
    >
      {dot ? <span aria-hidden="true" className={`h-2 w-2 ${RAIO.pilula} ${SOLIDO_DO_TOM[tone]}`} /> : null}
      {children}
    </span>
  );
}

const NOTICE_TONES: Record<Tone, { caixa: string; icone: string }> = {
  neutral: { caixa: 'border-slate-200 bg-slate-50 text-slate-800', icone: 'text-slate-500' },
  info: { caixa: 'border-sky-200 bg-sky-50 text-sky-900', icone: 'text-sky-600' },
  ok: { caixa: 'border-emerald-200 bg-emerald-50 text-emerald-900', icone: 'text-emerald-600' },
  warn: { caixa: 'border-amber-200 bg-amber-50 text-amber-900', icone: 'text-amber-600' },
  danger: { caixa: 'border-red-200 bg-red-50 text-red-900', icone: 'text-red-600' },
};

/**
 * Bloco de aviso. O ícone do tom não é enfeite: sem ele, informação e erro chegavam **só** pela cor
 * da caixa, e quem não distingue as duas via dois retângulos iguais com texto dentro.
 */
export function Notice({
  tone = 'info',
  title,
  children,
}: {
  tone?: Tone;
  title?: string;
  children: ReactNode;
}) {
  const estilo = NOTICE_TONES[tone];
  const Icone = ICONE_DO_TOM[tone];
  return (
    <div className={`flex gap-3 border p-3 ${PROSA} ${RAIO.controle} ${estilo.caixa}`}>
      <span className={`flex h-5 shrink-0 items-center ${estilo.icone}`}>
        <Icone size={16} />
      </span>
      <div className="min-w-0 flex-1">
        {title ? <p className="mb-1 font-semibold">{title}</p> : null}
        <div className="space-y-2">{children}</div>
      </div>
    </div>
  );
}

/**
 * Barra de progresso com nome acessível.
 *
 * O `role="progressbar"` do consumo de conversas tinha `aria-valuenow` mas nenhum rótulo: o leitor
 * de tela anunciava "82 por cento" de coisa nenhuma.
 */
export function ProgressBar({ value, label, tone = 'ok' }: { value: number; label: string; tone?: Tone }) {
  const pct = Math.max(0, Math.min(100, Math.round(value * 100)));
  return (
    <div
      role="progressbar"
      aria-label={label}
      aria-valuenow={pct}
      aria-valuemin={0}
      aria-valuemax={100}
      className={`h-2 w-full overflow-hidden bg-slate-200 ${RAIO.pilula}`}
    >
      <div
        className={`h-full transition-all ${RAIO.pilula} ${SOLIDO_DO_TOM[tone]}`}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

// --- estrutura -------------------------------------------------------------

/**
 * Cabeçalho de tela: título, uma linha de contexto e as ações.
 *
 * Existe para o fluxo de leitura ser o mesmo em toda tela larga — o olho aprende uma vez só onde é
 * o título e onde são as ações.
 */
export function PageHeader({
  title,
  description,
  actions,
}: {
  title: string;
  description?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-4">
      <div className="min-w-0">
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">{title}</h1>
        {description ? <p className={`mt-1 ${PROSA} text-slate-500`}>{description}</p> : null}
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
  );
}

/**
 * Card de seção: **uma responsabilidade por caixa**.
 *
 * É o primitivo de "região comum" da Gestalt, e é o que faltava — as telas eram sequências de
 * parágrafos e controles separados só por `space-y`, sem nada dizendo onde um assunto termina e o
 * próximo começa. Com fundo, borda e um título, cada assunto se identifica em menos de um segundo.
 */
export function Section({
  title,
  description,
  action,
  icon: Icone,
  children,
  padded = true,
}: {
  title?: string;
  description?: ReactNode;
  action?: ReactNode;
  icon?: (props: IconProps) => ReactNode;
  children: ReactNode;
  /** `false` quando o conteúdo é lista ou tabela e precisa encostar na borda. */
  padded?: boolean;
}) {
  return (
    <section className={`border border-slate-200 bg-white ${RAIO.card} ${SOMBRA.card}`}>
      {title ? (
        <header className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-200 px-4 py-3">
          <div className="flex min-w-0 gap-3">
            {Icone ? (
              <span className="flex h-6 shrink-0 items-center text-slate-500">
                <Icone size={17} />
              </span>
            ) : null}
            <div className="min-w-0">
              <h2 className="text-base leading-6 font-semibold text-slate-900">{title}</h2>
              {description ? <p className={`mt-1 ${PROSA} text-slate-500`}>{description}</p> : null}
            </div>
          </div>
          {action ? <div className="flex shrink-0 items-center gap-2">{action}</div> : null}
        </header>
      ) : null}
      <div className={padded ? 'space-y-4 p-4' : ''}>{children}</div>
    </section>
  );
}

/**
 * Título de grupo dentro de coluna estreita, onde um card dentro do outro pesaria demais. É a versão
 * leve do `Section`: separa por tipografia e uma linha, sem mais uma moldura.
 */
export function GroupTitle({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-2 border-b border-slate-200 pb-2">
      <h3 className="text-xs font-semibold tracking-wide text-slate-500 uppercase">{children}</h3>
      {action}
    </div>
  );
}

/**
 * Estado vazio com saída.
 *
 * O painel resolvia isto com um `<p>` cinza — "Nenhum contato ainda." — que lê como falha do sistema
 * em vez de estado normal. Com ícone, título e o próximo passo escrito, vira instrução.
 */
export function EmptyState({
  icon: Icone,
  title,
  description,
  action,
}: {
  icon?: (props: IconProps) => ReactNode;
  title: string;
  description?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-3 px-4 py-8 text-center">
      {Icone ? (
        <span className={`grid h-10 w-10 place-items-center bg-slate-100 text-slate-400 ${RAIO.card}`}>
          <Icone size={20} />
        </span>
      ) : null}
      <div className="space-y-1">
        <p className="text-sm font-semibold text-slate-800">{title}</p>
        {description ? <p className={`mx-auto max-w-md ${PROSA} text-slate-500`}>{description}</p> : null}
      </div>
      {action}
    </div>
  );
}

/**
 * Indicador numérico.
 *
 * O número vem antes do rótulo e em 24px porque é o que se lê primeiro numa varredura; o rótulo em
 * 12px maiúsculo é a legenda dele, não um par de igual peso. `tabular-nums` para os dígitos não
 * dançarem quando o valor muda.
 */
export function Stat({ label, value, hint }: { label: string; value: ReactNode; hint?: ReactNode }) {
  return (
    <div className={`border border-slate-200 bg-white p-4 ${RAIO.controle}`}>
      <p className="text-2xl font-semibold tabular-nums text-slate-900">{value}</p>
      <p className="mt-1 text-xs font-semibold tracking-wide text-slate-500 uppercase">{label}</p>
      {hint ? <p className={`mt-2 ${PROSA} text-slate-500`}>{hint}</p> : null}
    </div>
  );
}

// --- navegação -------------------------------------------------------------

/** Id do botão de uma aba. O painel de conteúdo aponta para ele via `aria-labelledby`. */
export const tabId = (id: string): string => `aba-${id}`;

/**
 * Abas com semântica de `tablist` **e** o teclado que a semântica promete.
 *
 * O visual já marcava a aba ativa por borda e cor — não só cor. Faltavam duas coisas: o leitor de
 * tela saber disso (sem `role` e `aria-selected` ele anunciava botões anônimos) e as setas
 * funcionarem. Num `tablist`, o Tab entra e sai do conjunto e são ←/→ que trocam de aba; sem isso
 * quem navega por teclado precisa tabular por todas as abas para chegar na última.
 */
export function Tabs<T extends string>({
  tabs,
  active,
  onChange,
  panelId,
}: {
  tabs: { id: T; label: string; icon?: (props: IconProps) => ReactNode }[];
  active: T;
  onChange(id: T): void;
  /** Id do elemento que mostra o conteúdo da aba. */
  panelId: string;
}) {
  const refs = useRef(new Map<T, HTMLButtonElement>());

  const irPara = (indice: number): void => {
    // Circular: da última, a seta para a direita volta à primeira, como manda o padrão ARIA.
    const alvo = tabs[((indice % tabs.length) + tabs.length) % tabs.length];
    if (!alvo) return;
    onChange(alvo.id);
    refs.current.get(alvo.id)?.focus();
  };

  const atual = tabs.findIndex((tab) => tab.id === active);

  return (
    <div
      role="tablist"
      className="flex flex-wrap gap-x-1 border-b border-slate-200 px-2"
      onKeyDown={(event) => {
        const passo: Record<string, number> = { ArrowRight: 1, ArrowLeft: -1 };
        if (event.key in passo) {
          event.preventDefault();
          irPara(atual + passo[event.key]!);
        }
        if (event.key === 'Home') {
          event.preventDefault();
          irPara(0);
        }
        if (event.key === 'End') {
          event.preventDefault();
          irPara(tabs.length - 1);
        }
      }}
    >
      {tabs.map((tab) => {
        const ativa = active === tab.id;
        return (
          <button
            key={tab.id}
            id={tabId(tab.id)}
            ref={(node) => {
              if (node) refs.current.set(tab.id, node);
              else refs.current.delete(tab.id);
            }}
            type="button"
            role="tab"
            aria-selected={ativa}
            aria-controls={panelId}
            // Só a aba ativa entra na ordem de tabulação; as outras chegam pelas setas.
            tabIndex={ativa ? 0 : -1}
            onClick={() => onChange(tab.id)}
            className={`inline-flex shrink-0 items-center gap-2 border-b-2 px-3 py-3 text-sm font-medium transition ${FOCO} ${
              ativa
                ? 'border-slate-900 text-slate-900'
                : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
          >
            {tab.icon ? <tab.icon size={15} /> : null}
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Controle segmentado: poucas opções mutuamente exclusivas que mudam a **mesma** área da tela.
 *
 * Diferente de `Tabs` de propósito. Abas navegam entre conteúdos distintos; um segmentado troca a
 * lente sobre um conteúdo só — computador/celular no preview. Usar o mesmo desenho para os dois
 * casos era o que fazia o seletor do preview parecer navegação sem navegar nada.
 */
export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  label,
}: {
  options: { id: T; label: string }[];
  value: T;
  onChange(id: T): void;
  label: string;
}) {
  return (
    <div role="radiogroup" aria-label={label} className={`inline-flex bg-slate-100 p-1 ${RAIO.controle}`}>
      {options.map((option) => {
        const ativo = value === option.id;
        return (
          <button
            key={option.id}
            type="button"
            role="radio"
            aria-checked={ativo}
            onClick={() => onChange(option.id)}
            className={`h-7 px-3 text-xs font-semibold transition ${RAIO.selo} ${FOCO} ${
              ativo ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Navegação lateral das configurações.
 *
 * Vertical, e não em abas, porque são cinco seções com nomes de tamanho desigual: em abas
 * horizontais elas quebravam em duas linhas e o último rótulo sumia — foi por isso que os rótulos
 * antigos foram encurtados para "Estilo" e "Instalar". Na vertical cada uma tem a largura que
 * precisa, e o nome pode voltar a ser o certo.
 */
export function SideNav<T extends string>({
  items,
  active,
  onChange,
  label,
}: {
  items: { id: T; label: string; icon?: (props: IconProps) => ReactNode }[];
  active: T;
  onChange(id: T): void;
  label: string;
}) {
  return (
    <nav aria-label={label} className="space-y-1">
      {items.map((item) => {
        const ativo = active === item.id;
        return (
          <button
            key={item.id}
            type="button"
            aria-current={ativo ? 'page' : undefined}
            onClick={() => onChange(item.id)}
            className={`flex w-full items-center gap-3 px-3 py-2 text-left text-sm font-medium transition ${RAIO.controle} ${FOCO} ${
              ativo ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
            }`}
          >
            {item.icon ? (
              <span className={ativo ? 'text-white' : 'text-slate-400'}>
                <item.icon size={16} />
              </span>
            ) : null}
            <span className="min-w-0 flex-1 truncate">{item.label}</span>
          </button>
        );
      })}
    </nav>
  );
}

// --- listas ----------------------------------------------------------------

/** Lista de strings editável — usada por domínios autorizados e allowlist de saída. */
export function StringList({
  values,
  onChange,
  placeholder,
  validate,
  addLabel = 'Adicionar',
}: {
  values: string[];
  onChange(next: string[]): void;
  placeholder: string;
  validate?(value: string): string | null;
  addLabel?: string;
}) {
  return (
    <div className="space-y-2">
      {values.map((value, index) => {
        const error = validate?.(value) ?? null;
        return (
          <div key={`${value}-${index}`} className="space-y-1">
            <div className="flex gap-2">
              <Input
                value={value}
                placeholder={placeholder}
                onChange={(event) => {
                  const next = [...values];
                  next[index] = event.target.value;
                  onChange(next);
                }}
                className={error ? 'border-red-300' : ''}
                aria-invalid={error ? true : undefined}
              />
              {/*
                Ícone no lugar da palavra "Remover": numa lista de cinco domínios, cinco botões de
                texto vermelho pesavam mais que a própria lista. O nome acessível continua completo.
              */}
              <IconButton
                icon={IconeLixeira}
                label={`Remover ${value || 'item vazio'}`}
                variant="danger"
                onClick={() => onChange(values.filter((_, i) => i !== index))}
              />
            </div>
            {error ? (
              <p role="alert" className={`${PROSA} text-red-700`}>
                {error}
              </p>
            ) : null}
          </div>
        );
      })}
      <Button onClick={() => onChange([...values, ''])}>
        <IconeMais />
        {addLabel}
      </Button>
    </div>
  );
}
